import { useState } from "react";
import api from "../../api";

export default function AdminUploadImage() {
  const [chapterId, setChapterId] = useState("");
  const [subchapterNumber, setSubchapterNumber] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [manualMode, setManualMode] = useState(false);
  const [pendingHotspots, setPendingHotspots] = useState([]);
  const [manualMessage, setManualMessage] = useState("");
  const [locked, setLocked] = useState(false);

  // Floating input forms
  const [floatingForm, setFloatingForm] = useState({
    visible: false,
    x: 0,
    y: 0,
    imgX: 0,
    imgY: 0,
    number: "",
    message: "",
  });

  const [manualFloatingForm, setManualFloatingForm] = useState({
    visible: false,
    number: "",
    message: "",
  });

  const [editForm, setEditForm] = useState({
    visible: false,
    groupIdx: null,
    coordIdx: null,
    x: 0,
    y: 0,
    message: "",
  });

  const handleFileChange = (e) => {
    if (locked) {
      alert("Image cannot be updated once uploaded.");
      return;
    }
    const f = e.target.files[0];
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  // On image click
  const handleImageClick = (e) => {
    if (!preview || locked) return;

    const rect = e.target.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width).toFixed(2);
    const y = ((e.clientY - rect.top) / rect.height).toFixed(2);

    if (manualMode) {
      setPendingHotspots([...pendingHotspots, { x, y }]);
    } else {
      setFloatingForm({
        visible: true,
        x: e.nativeEvent.offsetX,
        y: e.nativeEvent.offsetY,
        imgX: x,
        imgY: y,
        number: "",
        message: "",
      });
    }
  };

  // Save single hotspot
  const handleSaveHotspot = () => {
    if (!floatingForm.number || isNaN(floatingForm.number)) {
      alert("Invalid hotspot number!");
      return;
    }
    if (!floatingForm.message.trim()) {
      alert("Please enter a valid hotspot message!");
      return;
    }

    const newGroup = {
      hotspotNumber: parseInt(floatingForm.number),
      message: floatingForm.message.trim(),
      coords: [{ x: floatingForm.imgX, y: floatingForm.imgY }],
    };
    setHotspots([...hotspots, newGroup]);
    setFloatingForm({ ...floatingForm, visible: false });
  };

  const handleCancelFloating = () => {
    setFloatingForm({ ...floatingForm, visible: false });
  };

  // 🆕 Manual confirm floating form instead of prompt()
  const openManualFloatingForm = () => {
    if (pendingHotspots.length === 0) {
      alert("Please select at least one hotspot position.");
      return;
    }
    setManualFloatingForm({ visible: true, number: "", message: "" });
  };

  const handleSaveManualHotspots = () => {
    if (!manualFloatingForm.number || isNaN(manualFloatingForm.number)) {
      alert("Invalid hotspot number!");
      return;
    }
    if (!manualFloatingForm.message.trim()) {
      alert("Please enter a valid hotspot message!");
      return;
    }

    const newGroup = {
      hotspotNumber: parseInt(manualFloatingForm.number),
      message: manualFloatingForm.message.trim(),
      coords: pendingHotspots.map((h) => ({ x: h.x, y: h.y })),
    };

    setHotspots([...hotspots, newGroup]);
    setPendingHotspots([]);
    setManualMessage("");
    setManualMode(false);
    setManualFloatingForm({ visible: false });
  };

  // 🆕 Replace hotspot click edit/remove with floating form
  const handleHotspotClick = (groupIdx, coordIdx, event) => {
    if (locked) return;
    event.stopPropagation();

    const rect = event.target.getBoundingClientRect();
    setEditForm({
      visible: true,
      groupIdx,
      coordIdx,
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      message: hotspots[groupIdx].message,
    });
  };

  const handleEditSave = () => {
    const updated = [...hotspots];
    updated[editForm.groupIdx].message = editForm.message;
    setHotspots(updated);
    setEditForm({ ...editForm, visible: false });
  };

  const handleEditRemove = () => {
    const updated = [...hotspots];
    updated[editForm.groupIdx].coords.splice(editForm.coordIdx, 1);
    if (updated[editForm.groupIdx].coords.length === 0) {
      updated.splice(editForm.groupIdx, 1);
    }
    setHotspots(updated);
    setEditForm({ ...editForm, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !chapterId || !subchapterNumber) {
      alert("Please fill all required fields");
      return;
    }

    const formattedHotspots = hotspots.flatMap((group) =>
      group.coords.map((c) => ({
        hotspotNumber: group.hotspotNumber,
        label: `Hotspot ${group.hotspotNumber}`,
        content: group.message,
        shape: "circle",
        coordinates: [c.x, c.y],
      }))
    );

    const formData = new FormData();
    formData.append("file", file);
    formData.append("subchapterTitle", title);
    formData.append("chapterId", chapterId);
    formData.append("subchapterNumber", subchapterNumber);
    formData.append("hotspots", JSON.stringify(formattedHotspots));

    try {
      await api.post("/api/admins/subchapter/image-with-hotspots", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("✅ Image uploaded successfully! Further editing is locked.");
      setLocked(true);
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed!");
    }
  };

  return (
    <div className="container py-4">
      {/* Upload Form */}
      <div className="card shadow-lg">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Upload Image with Hotspots</h4>
          {locked && <span className="badge bg-danger">Editing Locked</span>}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Chapter ID</label>
              <input
                type="number"
                className="form-control"
                value={chapterId}
                onChange={(e) => setChapterId(e.target.value)}
                required
                disabled={locked}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Subchapter Number</label>
              <input
                type="number"
                className="form-control"
                value={subchapterNumber}
                onChange={(e) => setSubchapterNumber(e.target.value)}
                required
                disabled={locked}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={locked}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label">Select Image</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleFileChange}
                required={!locked}
                disabled={locked}
              />
            </div>
            <div className="col-12 d-flex justify-content-between">
              {!locked && (
                <>
                  <button type="submit" className="btn btn-success px-4">
                    Upload
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      manualMode ? "btn-danger" : "btn-outline-primary"
                    }`}
                    onClick={() => setManualMode(!manualMode)}
                  >
                    {manualMode ? "Exit Manual Mode" : "+ Add Hotspots Manually"}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Image Preview */}
      {preview && (
        <div className="mt-4 text-center">
          <div
            className="position-relative d-inline-block border rounded shadow-sm"
            style={{ maxWidth: "600px", position: "relative" }}
          >
            <img
              src={preview}
              alt="preview"
              className="img-fluid rounded"
              style={{ cursor: locked ? "not-allowed" : "crosshair" }}
              onClick={handleImageClick}
            />

            {/* Saved Hotspots */}
            {hotspots.map((group, gIdx) =>
              group.coords.map((c, i) => (
                <div
                  key={`g${gIdx}-c${i}`}
                  title={`${group.message} (Hotspot ${gIdx + 1})`}
                  onClick={(e) => handleHotspotClick(gIdx, i, e)}
                  className="position-absolute bg-danger border border-white rounded-circle"
                  style={{
                    left: `${c.x * 100}%`,
                    top: `${c.y * 100}%`,
                    transform: "translate(-50%, -50%)",
                    width: "14px",
                    height: "14px",
                    cursor: locked ? "not-allowed" : "pointer",
                  }}
                ></div>
              ))
            )}

            {/* Pending Hotspots */}
            {pendingHotspots.map((h, i) => (
              <div
                key={`pending-${i}`}
                className="position-absolute bg-warning border border-dark rounded-circle"
                style={{
                  left: `${h.x * 100}%`,
                  top: `${h.y * 100}%`,
                  transform: "translate(-50%, -50%)",
                  width: "16px",
                  height: "16px",
                }}
              ></div>
            ))}

            {/* Floating Add Form */}
           {/* Floating Input Box (larger + responsive) */}
{floatingForm.visible && (
  <div
    className="position-absolute hotspot-floating bg-white border shadow-lg p-3 rounded"
    style={{
      top: floatingForm.y + 20,
      left: floatingForm.x,
      transform: "translate(-50%, 0)",
      width: "min(480px, 90vw)",
      zIndex: 10,
    }}
  >
    <input
      type="number"
      className="form-control form-control-lg mb-3"
      placeholder="Hotspot Number"
      value={floatingForm.number}
      onChange={(e) =>
        setFloatingForm({ ...floatingForm, number: e.target.value })
      }
      style={{ fontSize: "1rem", padding: "10px" }}
    />
    <textarea
      rows="4"
      className="form-control form-control-lg mb-3"
      placeholder="Hotspot Message"
      value={floatingForm.message}
      onChange={(e) =>
        setFloatingForm({ ...floatingForm, message: e.target.value })
      }
      style={{ fontSize: "1rem", padding: "10px", resize: "vertical" }}
    />
    <div className="d-flex justify-content-end">
      <button
        type="button"
        className="btn btn-success btn-lg me-2"
        onClick={handleSaveHotspot}
      >
        Save
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-lg"
        onClick={handleCancelFloating}
      >
        Cancel
      </button>
    </div>
  </div>
)}

            {/* Edit/Remove Floating Form */}
            {/* Edit/Remove Floating Form (bigger & responsive) */}
{editForm.visible && (
  <div
    className="position-fixed hotspot-floating bg-white border shadow p-3 rounded"
    style={{
      top: editForm.y + 20,
      left: editForm.x,
      transform: "translate(-50%, 0)",
      width: "min(480px, 90vw)",
      zIndex: 11,
    }}
  >
    <label className="small text-muted">Edit message</label>
    <textarea
      rows="4"
      className="form-control form-control-lg mb-3"
      value={editForm.message}
      onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
      style={{ fontSize: "1rem", padding: "10px", resize: "vertical" }}
    />
    <div className="d-flex justify-content-end">
      <button
        type="button"
        className="btn btn-success btn-lg me-2"
        onClick={handleEditSave}
      >
        Save
      </button>
      <button
        type="button"
        className="btn btn-danger btn-lg me-2"
        onClick={handleEditRemove}
      >
        Remove
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-lg"
        onClick={() => setEditForm({ ...editForm, visible: false })}
      >
        Cancel
      </button>
    </div>
  </div>
)}

          </div>

          {/* Manual Floating Form */}
         {/* Manual Floating Form (when confirming multiple pending hotspots) */}
{manualMode && pendingHotspots.length > 0 && (
  <div className="mt-3">
    {!manualFloatingForm.visible ? (
      <div className="d-inline-block">
        <button
          className="btn btn-success btn-lg me-2"
          onClick={openManualFloatingForm}
        >
          Confirm All
        </button>
        <button
          className="btn btn-secondary btn-lg"
          onClick={() => setPendingHotspots([])}
        >
          Clear
        </button>
      </div>
    ) : (
      <div
        className="hotspot-floating bg-white border shadow p-3 rounded d-inline-block"
        style={{ width: "min(520px, 95vw)" }}
      >
        <input
          type="number"
          className="form-control form-control-lg mb-3"
          placeholder="Hotspot Number"
          value={manualFloatingForm.number}
          onChange={(e) =>
            setManualFloatingForm({ ...manualFloatingForm, number: e.target.value })
          }
          style={{ fontSize: "1rem", padding: "10px" }}
        />
        <textarea
          rows="4"
          className="form-control form-control-lg mb-3"
          placeholder="Hotspot Message"
          value={manualFloatingForm.message}
          onChange={(e) =>
            setManualFloatingForm({ ...manualFloatingForm, message: e.target.value })
          }
          style={{ fontSize: "1rem", padding: "10px", resize: "vertical" }}
        />
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-success btn-lg me-2"
            onClick={handleSaveManualHotspots}
          >
            Save
          </button>
          <button
            className="btn btn-secondary btn-lg"
            onClick={() => setManualFloatingForm({ visible: false })}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
  </div>
)}


          <p className="mt-2 text-muted">
            {locked
              ? "Image and hotspots are locked. Editing is disabled."
              : manualMode
              ? "Manual Mode: Click multiple spots, then confirm them with a single content."
              : "Click directly on the image to add hotspots (red dots). Click a red dot to edit or remove it."}
          </p>
        </div>
      )}
    </div>
  );
}
